import { DataTableColumnHeader } from '@optools/components/data-table/data-table-column-header';
import { DataTableViewOptions } from '@optools/components/data-table/data-table-view-options';
import { PageLoader } from '@optools/components/loading/page-loader';
import { Badge } from '@optools/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@optools/components/ui/table';
import { EditorMilestone } from '@optools/services/operator-gateway/milestones/types';
import { MilestoneType } from '@optools/services/operator-gateway/types/generated/graphql';
import { formatSecondsHHMMSS } from '@optools/utils/format';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { Ellipsis } from 'lucide-react';
import { useImperativeHandle, useMemo, useRef, useState } from 'react';

import { useMilestoneEditor } from '../context/milestone-editor-context';
import { formatInningDetails, formatVideoTimestamp } from '../utils/milestones';
import { AddMilestoneDialog } from './add-milestone-dialog';
import { DeleteMilestoneDialog } from './delete-milestone-dialog';
import { EditMilestoneDialog } from './edit-milestone-dialog';
import {
  StreamStartWarningTooltip,
  StreamStopWarningTooltip,
} from './milestone-table-warning-tooltips';

export interface MilestonesTableProps {
  loading: boolean;
  mediaId: string;
  milestones: EditorMilestone[] | null | undefined;
  programDateTime: string | undefined;
}

export function MilestonesTable({
  loading,
  mediaId,
  milestones,
  programDateTime,
}: MilestonesTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    GUID: false,
  });
  const [rowSelection, setRowSelection] = useState({});

  const { gridRef, seekTo, trimMode } = useMilestoneEditor();

  const columns: ColumnDef<EditorMilestone>[] = useMemo(() => {
    return [
      {
        accessorFn: (row) => {
          return milestones?.findIndex((milestone) => milestone.id === row.id);
        },
        header: ({ column }) => <DataTableColumnHeader column={column} title="ID" />,
        id: 'ID',
      },
      {
        accessorKey: 'id',
        enableSorting: false,
        header: 'GUID',
        id: 'GUID',
      },
      {
        accessorKey: 'inning',
        cell: (row) => {
          return (
            <div>
              <p>{formatInningDetails(row.cell.row.original)}</p>
            </div>
          );
        },
        enableSorting: false,
        header: 'Inning',
        id: 'Inning',
      },
      {
        accessorKey: 'milestoneType',
        cell: ({ cell }) => {
          const { absoluteTime, milestoneType, videoTime } = cell.row.original;

          return (
            <span className="flex items-center gap-1">
              {!trimMode && (
                <>
                  <StreamStartWarningTooltip
                    absoluteTime={absoluteTime}
                    milestoneType={milestoneType}
                  />
                  <StreamStopWarningTooltip milestoneType={milestoneType} videoTime={videoTime} />
                </>
              )}
              {milestoneType}
            </span>
          );
        },
        enableSorting: false,
        header: 'Type',
        id: 'Type',
      },
      {
        cell: ({ cell }) => {
          const { videoTime } = cell.row.original;

          if (typeof videoTime !== 'number') return 'Invalid Timestamp';

          if (!programDateTime) return <Ellipsis data-testid="timestamp-icon" />;

          return formatVideoTimestamp(videoTime);
        },
        enableSorting: false,
        header: 'Video Timestamp',
        id: 'Video Timestamp',
      },
      {
        accessorKey: 'absoluteTime',
        enableSorting: false,
        header: 'Absolute Time (UTC)',
        id: 'Absolute Time (UTC)',
      },
      {
        cell: ({ cell }) => {
          const { id, milestoneType, videoTime } = cell.row.original;

          const isStreamStartOrStop = [
            MilestoneType.STREAM_START,
            MilestoneType.STREAM_STOP,
          ].includes(milestoneType);

          return (
            <div className="ml-auto flex max-w-22 gap-2">
              <EditMilestoneDialog
                mediaId={mediaId}
                milestone={cell.row.original}
                originalTimestamp={
                  videoTime && videoTime >= 0 ? formatSecondsHHMMSS(videoTime) : '00:00:00'
                }
                programDateTime={programDateTime}
              />
              {!isStreamStartOrStop && <DeleteMilestoneDialog guid={id} mediaId={mediaId} />}
            </div>
          );
        },
        id: 'actions',
      },
    ];
  }, [mediaId, milestones, programDateTime, trimMode]);

  const data = useMemo(() => milestones ?? [], [milestones]);
  const rowRefs = useRef(new Array<HTMLTableRowElement | null>());

  // TanStack table requires that the data be in a stable state, so to allow the fallback array to work the data will Memoized
  // Link to github post: https://github.com/TanStack/table/issues/4614
  const table = useReactTable({
    columns,
    data,
    enableMultiRowSelection: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    state: {
      columnVisibility,
      rowSelection,
      sorting,
    },
  });

  useImperativeHandle(gridRef, () => ({
    scrollToIndexes: (index: number) => {
      setRowSelection({ [index]: true });
      const rowElement = rowRefs.current[index];
      if (rowElement) {
        rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
  }));

  return (
    <div className="relative" data-testid="milestones-table">
      <div className="mb-2 flex items-end justify-between gap-4 overflow-auto">
        <div className="flex items-center gap-2">
          <AddMilestoneDialog mediaId={mediaId} programDateTime={programDateTime} />
          <DataTableViewOptions table={table} />
        </div>
        {programDateTime && <Badge variant="outline">Program Date Time: {programDateTime}</Badge>}
      </div>
      {loading && <PageLoader className="absolute z-10 w-full flex-1 bg-background/80" />}
      <Table containerClassName="relative aspect-video overflow-auto rounded-md border">
        <TableHeader className="sticky top-0 border-b bg-background">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id} className="whitespace-nowrap">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, rowIndex) => (
              <TableRow
                key={row.id}
                ref={(el) => {
                  rowRefs.current[rowIndex] = el;
                }}
                className="cursor-pointer"
                data-state={row.getIsSelected() && 'selected'}
                onClick={() => {
                  setRowSelection({ [row.index]: true });
                  seekTo(row.original.videoTime ?? 0);
                }}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="py-2 pr-2 pl-4 whitespace-nowrap">
                    {/* Prevents rowClick functionality if the element inside the table cell is being clicked instead */}
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <span className="cursor-auto" onClick={(e) => e.stopPropagation()}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </span>
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell className="h-24 text-center" colSpan={columns.length}>
                No Milestones
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
